import { cn } from '@/utils/cn';
import { SeverityPill } from './SeverityPill';
import { useRelativeNow } from './RelativeTimeProvider';
import type { AlarmDisplayRow, AlarmRowVariant } from './types';

interface Props {
  alarm: AlarmDisplayRow;
  variant?: AlarmRowVariant;
  onClick?: (alarm: AlarmDisplayRow) => void;
  /**
   * Inject "now" for deterministic relative-time tests. When omitted, the row
   * follows the shared RelativeTimeProvider clock (and falls back to a
   * render-time Date.now() when no provider is mounted). Explicit prop wins.
   */
  now?: number;
  /**
   * Optional secondary line rendered beneath the row body. The site Alarms tab
   * uses it for a readable condition key; the portfolio hub omits it.
   */
  subLabel?: string;
  className?: string;
  /**
   * When true, the description wraps to multiple lines instead of truncating
   * with an ellipsis. Defaults to false — today's single-line truncate
   * behavior — because AlarmRow is shared by the site Alarms tab and the
   * portfolio alarms hub, two dense lists with no vertical room to grow;
   * changing their default would relayout both. Opt in from detail-style
   * surfaces that have the room, e.g. the SLD device card.
   *
   * The full description is also always exposed via `title` on the
   * description text (regardless of this prop) so truncated text is at
   * least reachable on hover/AT on every surface without changing layout.
   */
  wrapDescription?: boolean;
}

const BORDER_BY_SEVERITY = {
  critical: 'border-l-ems-red',
  warning: 'border-l-ems-amber',
  info: 'border-l-ems-blue',
} as const;

const PADDING_BY_VARIANT = {
  compact: 'px-2 py-1 text-xs',
  default: 'px-3 py-2 text-sm',
  dense: 'px-2 py-1.5 text-xs',
} as const;

export function AlarmRow({
  alarm,
  variant = 'default',
  onClick,
  now,
  subLabel,
  className,
  wrapDescription = false,
}: Props) {
  // Explicit `now` prop wins (deterministic tests); otherwise follow the shared
  // RelativeTimeProvider clock so the relative-time string re-renders on each
  // tick instead of freezing at mount (#242).
  const tickNow = useRelativeNow();
  const effectiveNow = now ?? tickNow;
  const ackedOpacity =
    alarm.status === 'acknowledged' || alarm.status === 'history' ? 'opacity-60' : '';
  const interactive = Boolean(onClick);

  const baseClasses = cn(
    'w-full text-left bg-ems-bg-card border border-ems-border rounded',
    'border-l-4',
    BORDER_BY_SEVERITY[alarm.severity],
    PADDING_BY_VARIANT[variant],
    ackedOpacity,
    interactive &&
      'hover:bg-ems-bg-card-hover focus:outline-none focus:ring-2 focus:ring-ems-blue/40 transition-colors',
    className,
  );

  const inner = (
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-2 min-w-0">
        {variant === 'compact' ? (
          <SeverityPill severity={alarm.severity} variant="icon" />
        ) : (
          <SeverityPill severity={alarm.severity} variant="pill" />
        )}
        <span
          title={alarm.description}
          className={cn(
            'font-medium text-ems-text-primary min-w-0',
            wrapDescription ? 'whitespace-normal break-words' : 'truncate',
          )}
        >
          {alarm.description}
        </span>
        {alarm.stale && (
          <span
            data-testid="stale-marker"
            title="Communication loss — last-known state"
            className="shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-ems-border text-ems-text-muted"
          >
            UNKNOWN
          </span>
        )}
        {variant !== 'compact' && (
          <span className="text-ems-text-muted truncate">{alarm.equipment}</span>
        )}
      </span>
      <span className="text-ems-text-muted whitespace-nowrap shrink-0">
        {formatRelative(alarm.occurredAt, effectiveNow)}
      </span>
    </div>
  );

  const body = subLabel ? (
    <div className="flex flex-col gap-0.5 w-full">
      {inner}
      <span
        data-testid="alarm-row-sublabel"
        className="font-mono text-[10px] text-ems-text-muted truncate"
      >
        {subLabel}
      </span>
    </div>
  ) : (
    inner
  );

  if (interactive) {
    return (
      <button
        type="button"
        onClick={() => onClick!(alarm)}
        aria-label={`Alarm: ${alarm.description}`}
        data-severity={alarm.severity}
        data-status={alarm.status}
        className={baseClasses}
      >
        {body}
      </button>
    );
  }

  return (
    <div data-severity={alarm.severity} data-status={alarm.status} className={baseClasses}>
      {body}
    </div>
  );
}

function formatRelative(d: Date, nowMs: number): string {
  const seconds = Math.max(0, Math.floor((nowMs - d.getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
